import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Users, Trophy, Check, Clock, Award, UserCheck, Settings, Plus, Trash2 } from "lucide-react";
import { getGiveaway, getGiveawayStatus, enterGiveaway, adminUpdateGiveaway } from "../services/giveaway";
import { useAuth } from "../context/AuthContext";
import Countdown from "../components/common/Countdown";
import Modal from "../components/common/Modal";
import { Badge } from "../components/common/Card";
import Reveal from "../components/common/Reveal";
import { IMAGES } from "../data/images";

export default function Giveaway() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [g, setG] = useState(null);
  const [entered, setEntered] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: "",
    prize_pool: 50,
    winner_slots: 5,
    goal: 1000,
    base_entries: 850,
    rules: [],
    recent_winners: [],
  });

  const load = () => {
    getGiveaway().then(setG).catch(() => {});
    getGiveawayStatus().then((s) => setEntered(s.entered)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const openEditModal = () => {
    if (!g) return;
    setEditForm({
      title: g.title || "Daily Giveaway",
      prize_pool: g.prize_pool || 50,
      winner_slots: g.winner_slots || 5,
      goal: g.goal || 1000,
      base_entries: g.base_entries || 850,
      rules: g.rules ? [...g.rules] : [],
      recent_winners: g.recent_winners ? g.recent_winners.map((w) => ({ ...w })) : [],
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await adminUpdateGiveaway({
        title: editForm.title.trim(),
        prize_pool: parseFloat(editForm.prize_pool) || 0,
        winner_slots: parseInt(editForm.winner_slots) || 1,
        goal: parseInt(editForm.goal) || 1000,
        base_entries: parseInt(editForm.base_entries) || 0,
        rules: editForm.rules.filter((r) => r.trim().length > 0),
        recent_winners: editForm.recent_winners.filter((w) => w.name && w.name.trim().length > 0),
      });
      setG(updated);
      setEditOpen(false);
      toast.success("Giveaway updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update giveaway");
    } finally {
      setSaving(false);
    }
  };

  const addRule = () => {
    setEditForm((prev) => ({ ...prev, rules: [...prev.rules, ""] }));
  };

  const updateRule = (idx, val) => {
    setEditForm((prev) => {
      const next = [...prev.rules];
      next[idx] = val;
      return { ...prev, rules: next };
    });
  };

  const removeRule = (idx) => {
    setEditForm((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== idx),
    }));
  };

  const addWinner = () => {
    setEditForm((prev) => ({
      ...prev,
      recent_winners: [...prev.recent_winners, { name: "", country: "IN", amount: "$10" }],
    }));
  };

  const updateWinner = (idx, field, val) => {
    setEditForm((prev) => {
      const next = [...prev.recent_winners];
      next[idx] = { ...next[idx], [field]: val };
      return { ...prev, recent_winners: next };
    });
  };

  const removeWinner = (idx) => {
    setEditForm((prev) => ({
      ...prev,
      recent_winners: prev.recent_winners.filter((_, i) => i !== idx),
    }));
  };

  const participate = async () => {
    try {
      const r = await enterGiveaway();
      setEntered(true);
      toast.success(r.message);
      load();
    } catch {
      toast.error("Could not enter right now.");
    }
  };

  if (!g) return null;
  const pct = Math.min(100, Math.round((g.entries / g.goal) * 100));

  return (
    <div className="mx-auto max-w-5xl pb-12">
      <Reveal>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
              <Gift size={14} /> Daily Reward
            </div>
            <h1 className="font-head text-2xl font-bold uppercase tracking-tight text-white md:text-3xl">{g.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <button
                onClick={openEditModal}
                className="btn-silver flex items-center gap-2 rounded-xl px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest"
                data-testid="admin-edit-giveaway-btn"
              >
                <Settings size={14} /> Edit Giveaway
              </button>
            )}
            <Badge tone="verified">Live</Badge>
          </div>
        </div>
      </Reveal>

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <div className="surface relative overflow-hidden rounded-2xl">
            <img src={IMAGES.giveawayCar} alt="Prize" className="h-64 w-full object-cover opacity-45 md:h-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Reward pool</div>
              <div className="font-head text-5xl font-black text-white brand-glow">${g.prize_pool}</div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-neutral-400">{g.winner_slots} winner slots</div>
            </div>
            {/* particles */}
            {Array.from({ length: 7 }).map((_, i) => (
              <span
                key={i}
                className="particle absolute bottom-4 h-1 w-1 rounded-full bg-white/50"
                style={{ left: `${10 + i * 12}%`, animationDelay: `${i * 0.8}s` }}
              />
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05} className="lg:col-span-5">
          <div className="surface flex h-full flex-col rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Ends in</div>
            <div className="mt-3"><Countdown endsAt={g.ends_at} /></div>

            <div className="mt-6 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-neutral-400">
              <span className="flex items-center gap-1.5"><Users size={12} /> {g.entries} entries</span>
              <span>Goal {g.goal}</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/6">
              <div className="h-full rounded-full bg-[#d4d8dc]" style={{ width: `${pct}%` }} />
            </div>

            <div className="mt-auto space-y-3 pt-6">
              <button
                onClick={participate}
                disabled={entered}
                className={`sweep flex w-full items-center justify-center gap-2 px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-widest transition-transform hover:-translate-y-0.5 ${
                  entered ? "rounded-xl border border-emerald-400/30 bg-emerald-400/5 text-emerald-300/90" : "btn-vip"
                }`}
                data-testid="giveaway-participate-btn"
              >
                {entered ? <><Check size={14} /> You're entered</> : <><Trophy size={14} /> Participate</>}
              </button>
              <button
                onClick={() => setRulesOpen(true)}
                className="btn-ghost w-full rounded-xl px-6 py-3 font-mono text-[11px] uppercase tracking-widest"
                data-testid="giveaway-rules-btn"
              >
                View Rules
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="surface h-full rounded-2xl p-6">
            <div className="mb-5 font-mono text-[10px] uppercase tracking-widest text-neutral-500">How it works</div>
            {[
              { icon: UserCheck, t: "Verify your account", d: "Verified traders qualify for the daily pool." },
              { icon: Trophy, t: "Tap participate", d: "One entry per day — takes a second." },
              { icon: Clock, t: "Draw at 00:00 UTC", d: `${g.winner_slots} winners are selected automatically.` },
              { icon: Award, t: "Get rewarded", d: `The $${g.prize_pool} pool is split between winners.` },
            ].map((s, i) => (
              <div key={s.t} className="flex items-start gap-4 border-t border-white/6 py-3.5 first:border-t-0 first:pt-0">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/10 text-[#d4d8dc]"><s.icon size={16} /></span>
                <div>
                  <div className="font-head text-sm font-bold uppercase tracking-tight text-white">{i + 1}. {s.t}</div>
                  <div className="mt-0.5 text-sm text-neutral-400">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="surface h-full rounded-2xl p-6">
            <div className="mb-5 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Recent winners</span>
              <Badge tone="verified">Live</Badge>
            </div>
            {(g.recent_winners || []).map((w, idx) => (
              <div key={idx} className="flex items-center justify-between border-t border-white/6 py-3 first:border-t-0 first:pt-0">
                <div className="flex items-center gap-3">
                  <span className="grid h-8 w-8 place-items-center rounded-full border border-white/10 font-mono text-[10px] text-neutral-400">
                    {(w.name || "").split(" ").map((x) => x[0]).join("")}
                  </span>
                  <span className="text-sm text-neutral-200">{w.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-600">{w.country}</span>
                </div>
                <span className="font-mono text-sm font-semibold text-[#8fd6b4]">{w.amount}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>


      {/* Rules Modal */}
      <Modal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        title="Giveaway rules"
        subtitle="Configurable — final rules set by admin."
        testid="giveaway-rules-modal"
        footer={<button onClick={() => setRulesOpen(false)} className="btn-silver rounded-xl px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest">Got it</button>}
      >
        <ul className="space-y-2.5">
          {(g.rules || []).map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-neutral-300">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#d4d8dc]" /> {r}
            </li>
          ))}
        </ul>
      </Modal>

      {/* Admin Edit Giveaway Modal (Admin only) */}
      {isAdmin && (
        <Modal
          open={editOpen}
          onClose={() => !saving && setEditOpen(false)}
          title="Edit Giveaway Configuration"
          subtitle="Changes are stored in MongoDB and live for all users."
          testid="admin-edit-giveaway-modal"
        >
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-neutral-400">Giveaway Title</label>
                <input
                  type="text"
                  required
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-xs text-white outline-none focus:border-[#d4d8dc]/60"
                  data-testid="admin-giveaway-title-input"
                />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-neutral-400">Reward Pool ($)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  required
                  value={editForm.prize_pool}
                  onChange={(e) => setEditForm({ ...editForm, prize_pool: e.target.value })}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-xs text-white outline-none focus:border-[#d4d8dc]/60"
                  data-testid="admin-giveaway-pool-input"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-neutral-400">Winner Slots</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={editForm.winner_slots}
                  onChange={(e) => setEditForm({ ...editForm, winner_slots: e.target.value })}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-xs text-white outline-none focus:border-[#d4d8dc]/60"
                  data-testid="admin-giveaway-slots-input"
                />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-neutral-400">Entry Goal</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={editForm.goal}
                  onChange={(e) => setEditForm({ ...editForm, goal: e.target.value })}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-xs text-white outline-none focus:border-[#d4d8dc]/60"
                  data-testid="admin-giveaway-goal-input"
                />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-neutral-400">Base Entries</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={editForm.base_entries}
                  onChange={(e) => setEditForm({ ...editForm, base_entries: e.target.value })}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 font-mono text-xs text-white outline-none focus:border-[#d4d8dc]/60"
                  data-testid="admin-giveaway-base-entries-input"
                />
              </div>
            </div>

            {/* Rules Section */}
            <div className="border-t border-white/8 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Giveaway Rules</label>
                <button
                  type="button"
                  onClick={addRule}
                  className="flex items-center gap-1 font-mono text-[10px] text-[#8fd6b4] hover:underline"
                >
                  <Plus size={12} /> Add Rule
                </button>
              </div>
              <div className="max-h-36 space-y-2 overflow-y-auto pr-1">
                {editForm.rules.map((rule, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={rule}
                      onChange={(e) => updateRule(idx, e.target.value)}
                      placeholder={`Rule #${idx + 1}`}
                      className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-xs text-white outline-none focus:border-[#d4d8dc]/60"
                    />
                    <button
                      type="button"
                      onClick={() => removeRule(idx)}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-rose-400/20 text-rose-400 hover:bg-rose-400/10"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Winners Section */}
            <div className="border-t border-white/8 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Recent Winners Showcase</label>
                <button
                  type="button"
                  onClick={addWinner}
                  className="flex items-center gap-1 font-mono text-[10px] text-[#8fd6b4] hover:underline"
                >
                  <Plus size={12} /> Add Winner
                </button>
              </div>
              <div className="max-h-36 space-y-2 overflow-y-auto pr-1">
                {editForm.recent_winners.map((win, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={win.name}
                      onChange={(e) => updateWinner(idx, "name", e.target.value)}
                      placeholder="Name (e.g. Rahul S.)"
                      className="w-1/2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 font-mono text-xs text-white outline-none"
                    />
                    <input
                      type="text"
                      value={win.country}
                      onChange={(e) => updateWinner(idx, "country", e.target.value.toUpperCase())}
                      placeholder="CC"
                      maxLength={3}
                      className="w-16 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-center font-mono text-xs text-white outline-none"
                    />
                    <input
                      type="text"
                      value={win.amount}
                      onChange={(e) => updateWinner(idx, "amount", e.target.value)}
                      placeholder="$10"
                      className="w-20 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 font-mono text-xs text-white outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => removeWinner(idx)}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-rose-400/20 text-rose-400 hover:bg-rose-400/10"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-white/8 pt-3">
              <button
                type="button"
                onClick={() => setEditOpen(false)}
                disabled={saving}
                className="btn-ghost rounded-xl px-4 py-2 font-mono text-xs uppercase"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-silver rounded-xl px-5 py-2 font-mono text-xs font-bold uppercase tracking-wider"
                data-testid="admin-save-giveaway-btn"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
